import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { HuntMap } from '@/components/map/HuntMap';
import { useMap } from '@/hooks/useMap';
import type { GameTrailMetadata, GeoJSONLineString } from '@/types/waypoint';

// ─── Constants ────────────────────────────────────────────────────────────────

const TRAIL_COLOR = '#92400E';
const SOURCE_ID = 'trail-preview-source';
const LINE_LAYER_ID = 'trail-preview-line';

// ─── TrailPreviewLayer — renders the path inside HuntMap's MapProvider ────────

interface TrailPreviewLayerProps {
  pathGeoJSON: GeoJSONLineString;
}

function TrailPreviewLayer({ pathGeoJSON }: TrailPreviewLayerProps) {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    function addLayer() {
      try {
        if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch { /* ignore */ }

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: pathGeoJSON,
          properties: {},
        },
      });

      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': TRAIL_COLOR,
          'line-width': 3,
          'line-opacity': 0.85,
        },
      });

      if (!fittedRef.current && pathGeoJSON.coordinates.length >= 2) {
        const lngs = pathGeoJSON.coordinates.map(([lng]) => lng);
        const lats = pathGeoJSON.coordinates.map(([, lat]) => lat);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 20, animate: false },
        );
        fittedRef.current = true;
      }
    }

    map.on('style.load', addLayer);
    if (map.isStyleLoaded()) addLayer();

    return () => {
      map.off('style.load', addLayer);
      try {
        if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch { /* map may be destroyed */ }
    };
  }, [map, pathGeoJSON]);

  return null;
}

// ─── Derive a display center from the path ───────────────────────────────────

function pathCenter(path: GeoJSONLineString): [number, number] {
  const lngs = path.coordinates.map(([lng]) => lng);
  const lats = path.coordinates.map(([, lat]) => lat);
  const midLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  // Return as [lat, lng] — app convention
  return [midLat, midLng];
}

// ─── GameTrailForm ────────────────────────────────────────────────────────────

interface GameTrailFormProps {
  metadata: Partial<GameTrailMetadata>;
  onChange: (updated: Partial<GameTrailMetadata>) => void;
  /** Anchor pin coordinates [lat, lng]. Optional — trail drawing works without it. */
  centerCoords?: [number, number];
  /** Current waypoint name — round-tripped through TrailDrawPage so it survives the remount. */
  locationName?: string;
}

export function GameTrailForm({ metadata, onChange, centerCoords, locationName }: GameTrailFormProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const hasPath = !!metadata.pathGeoJSON;

  // Center for the mini-map: prefer explicit pin, fall back to path midpoint
  const miniMapCenter: [number, number] =
    centerCoords ??
    (hasPath ? pathCenter(metadata.pathGeoJSON as GeoJSONLineString) : [39.5, -98.35]);

  function handleDrawTrail() {
    navigate('/locations/draw-trail', {
      state: {
        pathGeoJSON: metadata.pathGeoJSON,
        centerCoords,
        returnTo: location.pathname + location.search,
        locationName,
      },
    });
  }

  function handleClearPath() {
    onChange({ ...metadata, pathGeoJSON: undefined });
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="field-label">
        Trail Path (optional)
      </p>

      {hasPath ? (
        <>
          {/* Mini-map preview of the drawn trail */}
          <div
            className="relative rounded-lg overflow-hidden border border-[var(--color-border)]"
            style={{ height: '160px' }}
          >
            <HuntMap
              center={miniMapCenter}
              zoom={15}
              className="h-full w-full"
              initialStyle="satellite"
              requireTapToInteract={true}
            >
              {/* CAST: hasPath guard above ensures pathGeoJSON is defined */}
              <TrailPreviewLayer pathGeoJSON={metadata.pathGeoJSON as GeoJSONLineString} />
            </HuntMap>
          </div>

          {/* Edit / Clear buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDrawTrail}
              data-testid="edit-trail-button"
              className="px-3 py-1.5 rounded-md text-body-sm text-[var(--color-text-secondary)] border border-[var(--color-border-subtle)] hover:border-[var(--color-border)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              Edit Trail
            </button>
            <button
              type="button"
              onClick={handleClearPath}
              data-testid="clear-trail-button"
              className="px-3 py-1.5 rounded-md text-body-sm text-[var(--color-text-placeholder)] border border-[var(--color-border)] hover:border-[var(--color-border-subtle)] hover:text-[var(--color-text-muted)] transition-colors"
            >
              Clear
            </button>
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={handleDrawTrail}
            disabled={!centerCoords}
            data-testid="draw-trail-button"
            className="px-3 py-1.5 rounded-md text-body-sm text-[var(--color-text-secondary)] border border-[var(--color-border-subtle)] hover:border-[var(--color-border)] hover:text-[var(--color-text-primary)] transition-colors self-start disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Draw Trail
          </button>
          {!centerCoords && (
            <p data-testid="draw-trail-disabled-hint" className="text-body-xs text-[var(--color-text-placeholder)]">
              Pick a location on the map first.
            </p>
          )}
        </>
      )}
    </div>
  );
}

